// Ablauf eines Runs (R27): gemeinsame Bausteine für run.ts und events.ts (Zufall, Freunde, Heilen, Zeit, Kampfbeginn).
// Aufgeteilt aus run.ts (CLAUDE.md: Dateien über etwa 1500 Zeilen aufteilen).
import {
  specialNames,
  biomeOfDay,
  friendNames,
  getAbility,
  getArtifact,
  getItem,
  getSpecies,
  itemUse,
  runRules,
  systemOn,
  species as allSpecies,
  type EventEffect,
  type EventOption,
  type RunRules,
} from '../../content/index.ts';
import { createRng, type Rng } from '../rng.ts';
import type { InfestedSpec } from '../types.ts';
import { generateFoes, type FoeRequest } from './foes.ts';
import {
  choicesBetween,
  combatProfile,
  levelChoices,
  levelForXp,
  maxHp,
  maxTreeHp,
  xpForLevel,
} from './profile.ts';
import { rollFriend } from '../luck.ts';
import { pickIntro } from './bonds.ts';
import { stormStage } from './river.ts';
import type {
  ChronicleEntry,
  FightContext,
  RunFriend,
  RunState,
} from './types.ts';

/** Zufall läuft über den gespeicherten Zustand, damit ein geladener Run genau so weitergeht */
export function withRng<T>(run: RunState, f: (rng: Rng) => T): T {
  const rng = createRng(run.rng);
  const out = f(rng);
  run.rng = rng.state();
  return out;
}

export const pick = <T>(rng: Rng, list: T[]): T => list[Math.floor(rng.next() * list.length)]!;

/** K4: Feldplätze nach Tag; am Durchlass einer mehr für den Boss (R27, Vorschlag Fabi im Spieltest) */
export function fieldSlotsOf(run: Pick<RunState, 'day'> & Partial<Pick<RunState, 'landing' | 'landings'>>, rules: RunRules = runRules): number {
  const base = rules.fieldSlots[Math.min(run.day, rules.fieldSlots.length) - 1]!;
  return run.landing !== undefined && run.landings && run.landing >= run.landings.length ? base + rules.bossExtraSlot : base;
}

export function stageNow(run: Pick<RunState, 'lead'>): string {
  return stormStage(run.lead);
}

/**
 * R38 (Fabi, Phase Kern): das Unwetter als Uhr. Aus heißt: die alte Sturmuhr (Schalter „Alte Sturmuhr“), mit Zugewinn
 * beim Vorbeitreiben und Sturmwesen, die mit dem Sturm mehr werden
 */
export const stormClockOn = (): boolean => !systemOn('stormDrift');

/** R38: Vorsprung am Morgen */
export const morningLead = (): number => (stormClockOn() ? runRules.storm.clock.startLead : runRules.storm.startLead + runRules.storm.driftGain);

/** R38: Mit welcher Sturmstufe die Gegner gerechnet werden; mit dem Unwetter als Uhr werden sie durch den Sturm nicht mehr */
export const budgetStage = (stage: string): string => (stormClockOn() ? 'ruhig' : stage);

/** R38: Morgens erfahrt ihr, was am Durchlass wartet */
export function scoutGate(run: RunState): void {
  if (!stormClockOn()) return;
  run.caught = false;
  withRng(run, (rng) => {
    run.bossPreview = { day: run.day, enemies: generateFoes(rng, bossRequest(run)), strength: 1 };
  });
}

/** R38: So viel Kraft haben die Sturmwesen am Durchlass noch (1 voll) */
export const gateStrength = (run: Pick<RunState, 'bossPreview' | 'day'>): number => (run.bossPreview?.day === run.day ? (run.bossPreview.strength ?? 1) : 1);

/** Q44: Hinter der letzten Anlegestelle des Tages wartet der Durchlass */
export function isBossLanding(run: Pick<RunState, 'landing' | 'landings'>): boolean {
  return run.landing >= run.landings.length;
}

/** Wer gerade an Bord ist und mit an Land kann (E4: nicht, wer unterwegs ist) */
export function available(run: RunState): RunFriend[] {
  return run.crew.filter((f) => !f.away);
}

export function friendById(run: RunState, id: string): RunFriend {
  const f = run.crew.find((x) => x.id === id);
  if (!f) throw new Error(`Kein Freund mit ${id} an Bord`);
  return f;
}

export function chronicle(run: RunState, title: string, line: string, extra: Partial<ChronicleEntry> = {}): void {
  run.chronicle.push({ day: run.day, landing: Math.min(run.landing, run.landings.length) + 1, title, line, stage: stageNow(run), ...extra });
}

export function regenMul(run: RunState): number {
  let m = 1;
  for (const id of run.artifacts) for (const e of getArtifact(id).effects) if ('run' in e && e.run === 'regen') m *= e.mul ?? 1;
  return m;
}

export function heal(run: RunState, f: RunFriend, share: number): void {
  const max = maxHp(f, run);
  f.hp = Math.min(max, f.hp + max * share);
}

/** Das schwächste Heilmittel im Gepäck, oder null */
export function weakestHealItem(run: Pick<RunState, 'inventory'>): string | null {
  const heals = run.inventory.filter((id) => (getItem(id).use?.heal ?? 0) > 0);
  heals.sort((a, b) => (itemUse(a).heal ?? 0) - (itemUse(b).heal ?? 0));
  return heals[0] ?? null;
}

/** S5, K5: Der Baum erholt sich mit; ein gefällter wächst auf ein Viertel nach */
export function regrowTree(run: RunState, f: RunFriend, share: number): void {
  const max = maxTreeHp(f, run);
  if (max === null) return;
  const now = f.treeHp ?? max;
  f.treeHp = Math.min(max, now <= 0 ? max * runRules.regen.treeRegrow : now + max * share);
}

export function freshTally() {
  return { fights: 0, wins: 0, damage: 0, taken: 0, healed: 0, shielded: 0, kills: 0, kniffs: 0, xp: 0, landings: 0 };
}

/** F33, Q38: Fabis Namen; besondere Namen nur für Spezies mit passenden Merkmalen (Schalk für Gerissene usw.) */
export function namePool(species: string): string[] {
  const tags = getSpecies(species).nameTags ?? [];
  return [...friendNames, ...specialNames.filter((n) => n.needs.every((t) => tags.includes(t))).map((n) => n.name)];
}

/** F33: zufälliger Name aus Fabis Liste, möglichst noch nicht vergeben; Kosenamen nur für niedliche Spezies (Q38) */
export function newName(run: RunState, rng: Rng, species: string): string {
  // Spieltest Phase 4 (Fabi): nie ein Name, den schon jemand trägt, auch nicht ein wartender Neuer
  const p = run.pending;
  const waiting = [...run.recruitQueue, ...(p.kind === 'recruit' ? [p.candidate] : p.kind === 'event' && p.subject ? [p.subject] : [])];
  const taken = new Set([...run.crew, ...run.gone, ...waiting].map((f) => f.name));
  const pool = namePool(species);
  const free = pool.filter((n) => !taken.has(n));
  return pick(rng, free.length ? free : pool);
}

/**
 * T37 (Fabi, 17.10.2026 nachts): das kleinste Aussehen, das in der Crew noch kein Freund derselben Spezies trägt. Ohne
 * Zufall, damit die Simulation gleich bleibt
 */
export function freeLook(run: RunState, species: string, except?: RunFriend): number {
  const taken = new Set(run.crew.filter((o) => o !== except && o.species === species).map((o) => o.look ?? -1));
  let look = 0;
  while (taken.has(look)) look++;
  return look;
}

/** T37: Gleichen sich zwei Freunde derselben Spezies, bekommt der spätere ein freies Aussehen (auch für ältere Spielstände) */
export function ensureLooks(run: RunState): void {
  const seen = new Map<string, Set<number>>();
  for (const f of run.crew) {
    const used = seen.get(f.species) ?? new Set<number>();
    if (f.look === undefined || used.has(f.look)) {
      let look = 0;
      while (used.has(look)) look++;
      f.look = look;
    }
    used.add(f.look);
    seen.set(f.species, used);
  }
}

/** Ein neuer Freund. Kommt er schon gelevelt (E6), sind seine Wahlen schon getroffen: das ist sein Charakter. */
export function makeFriend(run: RunState, rng: Rng, opts: { species?: string; friendClass?: string; level?: number; how: RunFriend['origin']['how'] }): RunFriend {
  const sp = opts.species ? getSpecies(opts.species) : pick(rng, allSpecies);
  const cls = opts.friendClass ?? pick(rng, sp.classes).friendClass;
  const level = opts.level ?? 1;
  const f: RunFriend = {
    id: `f${run.nextId++}`,
    name: newName(run, rng, sp.id),
    titles: [],
    species: sp.id,
    friendClass: cls,
    level,
    xp: xpForLevel(level),
    hp: 0,
    treeHp: null,
    branch: null,
    nodes: [],
    pendingLevels: choicesBetween(1, level),
    items: [],
    xpBoost: 0,
    away: null,
    origin: { how: opts.how, day: run.day, landing: run.landing + 1 },
    tally: freshTally(),
    gone: null,
    // F37 (Fabi, Runde 7): eigene Anlagen, damit kein Dachs wie der andere ist
    rolls: rollFriend(rng),
    look: freeLook(run, sp.id),
  };
  while (f.pendingLevels > 0) {
    const choices = levelChoices(f);
    if (choices.length === 0) break;
    applyLevelChoice(f, pick(rng, choices).id);
  }
  f.pendingLevels = 0;
  f.hp = maxHp(f, run);
  f.treeHp = maxTreeHp(f, run);
  // F40 (Lead-Auftrag): Jeder neue Freund stellt sich vor
  const intro = pickIntro(run, f);
  if (intro) f.intro = intro;
  return f;
}

export function applyLevelChoice(f: RunFriend, id: string): void {
  if (!f.branch) f.branch = id;
  else f.nodes.push(id);
  f.pendingLevels -= 1;
}

/** F8, F35: eine offene Level-Wahl treffen; was man nicht nimmt, ist für diesen Freund weg */
export function chooseLevel(run: RunState, friendId: string, choiceId: string): void {
  const f = friendById(run, friendId);
  const choices = levelChoices(f);
  if (!choices.some((c) => c.id === choiceId)) throw new Error(`${choiceId} steht für ${f.name} nicht zur Wahl`);
  const before = maxHp(f, run);
  applyLevelChoice(f, choiceId);
  f.hp += maxHp(f, run) - before;
}

/** F12: XP gutschreiben, Level-Ups merken; mehr Leben gibt es mit dem Level sofort dazu */
export function gainXp(run: RunState, f: RunFriend, xp: number): boolean {
  if (xp <= 0) return false;
  const before = maxHp(f, run);
  const oldLevel = f.level;
  f.xp += xp;
  f.tally.xp += xp;
  f.level = levelForXp(f.xp);
  if (f.level === oldLevel) return false;
  f.pendingLevels += choicesBetween(oldLevel, f.level);
  f.hp += maxHp(f, run) - before;
  return true;
}

/** F21, P3: Abschied oder zurückgelassen */
export function leave(run: RunState, f: RunFriend, how: NonNullable<RunFriend['gone']>['how'], text: string): void {
  run.crew = run.crew.filter((x) => x.id !== f.id);
  f.gone = { day: run.day, landing: Math.min(run.landing, run.landings.length) + 1, how, text };
  f.away = null;
  run.gone.push(f);
  if (run.crew.length === 0) {
    // R24: Ohne Freunde ist der Run verloren
    run.status = 'lost';
    run.pending = { kind: 'end' };
    chronicle(run, 'Ende der Reise', 'Niemand ist mehr an Bord. Der Run ist verloren.');
  }
}

/** Q39 (Fabi): Was man am Ufer tut, kostet Vorsprung, in Stunden */
export function spend(run: RunState, hours: number): void {
  run.lead = Math.max(0, run.lead - hours);
}

/**
 * Q39: Wie lange eine Ereignis-Option dauert. Weiterfahren kostet nichts; was etwas bewirkt, dauert ein bisschen,
 * wenn die Daten nichts anderes sagen. Versuche (E26) zählen einzeln.
 */
export function optionTime(o: EventOption): number {
  if (o.time !== undefined) return o.time;
  if (o.attempt) return 0;
  const any = (o.effects?.length ?? 0) > 0 || (o.outcomes ?? []).some((x) => x.effects.length > 0);
  return any ? runRules.storm.time.option : 0;
}

export function infestedSpec(run: RunState, f: RunFriend): InfestedSpec {
  return { species: f.species, friendClass: f.friendClass, name: f.name, level: f.level, profile: combatProfile(f, run) };
}

/** E31: der Bosskampf des Tages, wie er auch für die Späher aussieht */
export function bossRequest(run: RunState): FoeRequest {
  return { day: run.day, biome: biomeOfDay(run.day).id, fieldSlots: fieldSlotsOf({ day: run.day, landing: run.landings.length, landings: run.landings }), stage: budgetStage(stageNow(run)), boss: true };
}

export function startFight(run: RunState, party: string[], kind: FightContext['kind'], subject?: RunFriend, opts: NonNullable<EventEffect['fight']> = {}): void {
  // Q39: Ein Kampf kostet Zeit; die Stufe im Kampf ist die nach dem Kampf, wie bisher bei einem Halt von einer Stunde
  spend(run, runRules.storm.time.fight);
  const stage = stageNow(run);
  withRng(run, (rng) => {
    let infested: RunFriend | undefined;
    if (kind === 'befallene') {
      const [lo, hi] = runRules.enemyBudget.infestedLevel[run.day - 1] ?? [2, 2];
      infested = makeFriend(run, rng, { level: lo! + Math.floor(rng.next() * (hi! - lo! + 1)), how: 'befreit' });
    }
    const levels = party.map((id) => friendById(run, id).level);
    // Runde 7: Folgen vom Ufer, die Kämpfe verändern: Hagelchen-Nest (mehr oder schwächere Hagelchen), zeternde Amseln
    const extra: string[] = [];
    const notes: string[] = [];
    if (run.flags['tag:hagelnest']) {
      extra.push('hagelchen');
      notes.push('Aus dem Hagelchen-Nest, das ihr stehen gelassen habt, flattert ein Hagelchen mehr heran.');
    }
    if (run.flags['tag:amseln-zetern']) {
      extra.push(pick(rng, ['schlammling', 'windschnitt', 'hagelchen']));
      notes.push('Das Gezeter der Amseln hat ein Sturmwesen mehr angelockt.');
      delete run.flags['tag:amseln-zetern'];
    }
    if (run.flags['tag:hagelnest-zerstoert']) notes.push('Ohne ihr Nest sind die Hagelchen heute schwächer.');
    const preview = kind === 'boss' && run.bossPreview?.day === run.day ? run.bossPreview.enemies : null;
    // R38: Jeder gewonnene Kampf unterwegs hat die Sturmwesen am Durchlass geschwächt; wer eingeholt wurde, trifft sie mit voller Kraft
    const strength = stormClockOn() && !run.caught ? gateStrength(run) : 1;
    const enemies = preview ? preview.map((e) => ({ ...e, ...(strength < 1 ? { statFactor: (e.statFactor ?? 1) * strength } : {}) })) : generateFoes(rng, {
      day: run.day,
      biome: biomeOfDay(run.day).id,
      fieldSlots: fieldSlotsOf(run),
      stage: budgetStage(stage),
      partyLevel: levels.reduce((a, l) => a + l, 0) / Math.max(1, levels.length),
      infested: infested ? infestedSpec(run, infested) : undefined,
      boss: kind === 'boss',
      ambush: kind === 'hinterhalt',
      // S8 Diebische Elster: Ist sie dabei, tragen Sturmwesen öfter ein Item
      itemFactor: party.some((id) => getSpecies(friendById(run, id).species).passive === 'diebische-elster') ? getAbility('diebische-elster').params.itemChanceFactor! : 1,
      starFactor: starFactor(run),
      // E30, E34 (Fabi, Runde 7): Hinterhalte sind stärker, das Nest besteht nur aus jungen Hagelchen
      statBoost: (1 + (opts.stronger ?? 0)) * (opts.young ?? 1),
      ...(opts.only ? { only: opts.only } : {}),
      ...(opts.count ? { budgetFactor: opts.count } : {}),
      extra,
      weaken: run.flags['tag:hagelnest-zerstoert'] ? { hagelchen: 0.85 } : {},
    });
    const xpFactor = (1 + runRules.xp.perDayBonus * (run.day - 1)) * (kind === 'boss' ? runRules.xp.bossFactor : 1);
    const fight: FightContext = { kind, enemies, stage, fieldSlots: fieldSlotsOf(run), xpFactor, seed: Math.floor(rng.next() * 2 ** 31) };
    if (opts.onWin) fight.onWin = opts.onWin;
    if (notes.length) fight.notes = notes;
    if (infested) fight.infested = infested;
    if (subject) fight.subject = subject;
    run.pending = { kind: 'fight', fight, party };
  });
}

/** Spieltest Phase 4 (Fabi): Wer mit will, trägt keinen Namen, den an Bord schon jemand hat */
export function uniqueName(run: RunState, f: RunFriend): RunFriend {
  if (run.crew.some((c) => c.name === f.name)) f.name = withRng(run, (rng) => newName(run, rng, f.species));
  return f;
}

/** S8 (Fabi, Runde 8): ein Wert der Diebischen Elster, wenn eine an Bord ist, sonst der Grundwert */
export function elsterParam(run: RunState, key: string, base: number): number {
  const elster = run.crew.some((f) => !f.gone && !f.away && getSpecies(f.species).passive === 'diebische-elster');
  return elster ? (getAbility('diebische-elster').params[key] ?? base) : base;
}

/** I27, S8: Mit der Elster an Bord sind gefundene Items öfter besonders */
export function starFactor(run: RunState): number {
  return elsterParam(run, 'starChanceFactor', 1);
}
